import SectionHead, { sectionText } from "./SectionHead";

// Contact on a Custom page: phone, website, Instagram and TikTok, each only
// when the pro filled it in. Call opens the dialer; the rest open in a new
// tab. It never shows an address: the street address stays private, and this
// section doesn't read the location at all. With none of the four, there's no
// section.

const Icon = ({ size = 18, children }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.25"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    {children}
  </svg>
);

const PHONE_PATH =
  "M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.7a2 2 0 0 1-.5 2.1L8 9.8a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.7.7a2 2 0 0 1 1.7 2z";

const PhoneIcon = ({ size }) => (
  <Icon size={size}>
    <path d={PHONE_PATH} />
  </Icon>
);

const GlobeIcon = () => (
  <Icon>
    <circle cx="12" cy="12" r="10" />
    <path d="M2 12h20" />
    <path d="M12 2a15 15 0 0 1 0 20a15 15 0 0 1 0-20" />
  </Icon>
);

const InstagramIcon = () => (
  <Icon>
    <rect x="3" y="3" width="18" height="18" rx="5" />
    <circle cx="12" cy="12" r="4" />
    <path d="M17.5 6.5h.01" />
  </Icon>
);

const TikTokIcon = () => (
  <Icon>
    <path d="M9 12a4 4 0 1 0 4 4V3c.8 2.6 2.8 4.2 6 4.4" />
  </Icon>
);

const ArrowOutIcon = () => (
  <Icon size={16}>
    <path d="M7 17 17 7" />
    <path d="M9 7h8v8" />
  </Icon>
);

// Same rule the server saves handles with.
const HANDLE_PATTERN = /^[A-Za-z0-9._]{1,30}$/;

const readHandle = (value) => {
  const handle = sectionText(value).replace(/^@/, "");
  return HANDLE_PATTERN.test(handle) ? handle : "";
};

// The server stores the website as a full http(s) link; anything else is
// left off rather than linked.
const readWebsite = (value) => {
  const website = sectionText(value);
  return /^https?:\/\/[^\s]+$/i.test(website) ? website : "";
};

const displayWebsite = (url) =>
  url.replace(/^https?:\/\//i, "").replace(/^www\./i, "").replace(/\/$/, "");

export const buildContactLinks = ({ phone, website, instagramHandle, tiktokHandle }) => {
  const links = [];
  const phoneText = sectionText(phone);
  const dialable = phoneText.replace(/[^\d+]/g, "");
  if (dialable) {
    links.push({
      key: "phone",
      label: "Call",
      value: phoneText,
      href: `tel:${dialable}`,
      icon: <PhoneIcon />,
      trailing: <PhoneIcon size={16} />,
    });
  }
  const websiteUrl = readWebsite(website);
  if (websiteUrl) {
    links.push({
      key: "website",
      label: "Website",
      value: displayWebsite(websiteUrl),
      href: websiteUrl,
      external: true,
      icon: <GlobeIcon />,
    });
  }
  const instagram = readHandle(instagramHandle);
  if (instagram) {
    links.push({
      key: "instagram",
      label: "Instagram",
      value: `@${instagram}`,
      href: `https://www.instagram.com/${instagram}/`,
      external: true,
      icon: <InstagramIcon />,
    });
  }
  const tiktok = readHandle(tiktokHandle);
  if (tiktok) {
    links.push({
      key: "tiktok",
      label: "TikTok",
      value: `@${tiktok}`,
      href: `https://www.tiktok.com/@${tiktok}`,
      external: true,
      icon: <TikTokIcon />,
    });
  }
  return links;
};

const ProfileContactSection = ({ sectionId = "contact", content, contact }) => {
  const links = buildContactLinks(contact || {});
  if (links.length === 0) return null;

  const heading = sectionText(content?.heading) || "Contact";
  const headingId = `profile-section-${sectionId}-heading`;

  return (
    <section className="profile-block profile-contact" aria-labelledby={headingId}>
      <SectionHead id={headingId} heading={heading} />
      <ul className="profile-contact__list">
        {links.map((link) => (
          <li key={link.key} className="profile-contact__item">
            <a
              className="profile-contact__link"
              href={link.href}
              {...(link.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
            >
              <span className="profile-contact__icon">{link.icon}</span>
              <span className="profile-contact__text">
                <span className="profile-contact__label">{link.label}</span>
                <span className="profile-contact__value" title={link.value}>
                  {link.value}
                </span>
                {link.external && <span className="profile-block__sr"> (opens in a new tab)</span>}
              </span>
              <span className="profile-contact__trailing">
                {link.trailing || <ArrowOutIcon />}
              </span>
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
};

export default ProfileContactSection;
